import {
  Component,
  Show,
  createMemo,
  createEffect,
  createSignal,
} from "solid-js";
import { useSearchParams } from "@solidjs/router";
import { SearchInput } from "../ui/SearchInput";
import { MultiSelect } from "../ui/MultiSelect";
import type { MultiSelectOption } from "../ui/MultiSelect";
import { FilterChips } from "./FilterChips";
import type { FilterChip } from "./FilterChips";
import { TypeIcon } from "../work-items/TypeIcon";
import type { WorkItemType, WorkItemStatus } from "../../api";

export interface BacklogFilters {
  typeIds: string[];
  statusIds: string[];
  search: string;
}

export interface FilterBarProps {
  itemTypes: WorkItemType[];
  statuses: WorkItemStatus[];
  onFilterChange: (filters: BacklogFilters) => void;
  /** Incrementing number that triggers clearing all filters from the parent */
  clearSignal?: number;
}

export const FilterBar: Component<FilterBarProps> = (props) => {
  const [searchParams, setSearchParams] = useSearchParams<{
    type?: string;
    status?: string;
    q?: string;
  }>();

  const parseTypesFromUrl = (): string[] => {
    if (!searchParams.type) return [];
    const names = searchParams.type.split(",").filter(Boolean);
    return names
      .map((name) => {
        const t = props.itemTypes.find(
          (t) => t.name.toLowerCase() === name.toLowerCase(),
        );
        return t?.id;
      })
      .filter((id): id is string => !!id);
  };

  const parseStatusesFromUrl = (): string[] => {
    if (!searchParams.status) return [];
    return searchParams.status.split(",").filter((id) => {
      return props.statuses.some((s) => s.id === id);
    });
  };

  const [selectedTypeIds, setSelectedTypeIds] =
    createSignal<string[]>(parseTypesFromUrl());
  const [selectedStatusIds, setSelectedStatusIds] = createSignal<string[]>(
    parseStatusesFromUrl(),
  );
  const [searchValue, setSearchValue] = createSignal(searchParams.q ?? "");

  const currentFilters = createMemo(
    (): BacklogFilters => ({
      typeIds: selectedTypeIds(),
      statusIds: selectedStatusIds(),
      search: searchValue(),
    }),
  );

  const hasActiveFilters = createMemo(
    () =>
      selectedTypeIds().length > 0 ||
      selectedStatusIds().length > 0 ||
      searchValue().length > 0,
  );

  // Sync filters to URL and notify parent
  const syncToUrl = (filters: BacklogFilters) => {
    const typeNames = filters.typeIds
      .map((id) => props.itemTypes.find((t) => t.id === id)?.name.toLowerCase())
      .filter(Boolean)
      .join(",");

    setSearchParams(
      {
        type: typeNames || undefined,
        status: filters.statusIds.join(",") || undefined,
        q: filters.search || undefined,
      },
      { replace: true },
    );
  };

  createEffect(() => {
    const filters = currentFilters();
    props.onFilterChange(filters);
  });

  const typeOptions = createMemo((): MultiSelectOption[] =>
    props.itemTypes.map((t) => ({
      value: t.id,
      label: t.name,
      icon: <TypeIcon typeName={t.name} />,
    })),
  );

  const statusOptions = createMemo((): MultiSelectOption[] =>
    props.statuses
      .filter((s) => s.category !== "done")
      .map((s) => ({
        value: s.id,
        label: s.name,
        icon: (
          <span
            class={
              "w-2.5 h-2.5 rounded-full flex-shrink-0 " +
              statusDotColor(s.category)
            }
          />
        ),
      })),
  );

  const chips = createMemo((): FilterChip[] => {
    const result: FilterChip[] = [];

    for (const typeId of selectedTypeIds()) {
      const t = props.itemTypes.find((t) => t.id === typeId);
      if (t) {
        result.push({
          key: `type:${typeId}`,
          category: "Type",
          label: t.name,
        });
      }
    }

    for (const statusId of selectedStatusIds()) {
      const s = props.statuses.find((s) => s.id === statusId);
      if (s) {
        result.push({
          key: `status:${statusId}`,
          category: "Status",
          label: s.name,
        });
      }
    }

    if (searchValue()) {
      result.push({
        key: "search",
        category: "Search",
        label: searchValue(),
      });
    }

    return result;
  });

  const handleTypeChange = (ids: string[]) => {
    setSelectedTypeIds(ids);
    syncToUrl({ ...currentFilters(), typeIds: ids });
  };

  const handleStatusChange = (ids: string[]) => {
    setSelectedStatusIds(ids);
    syncToUrl({ ...currentFilters(), statusIds: ids });
  };

  const handleSearchChange = (value: string) => {
    setSearchValue(value);
    syncToUrl({ ...currentFilters(), search: value });
  };

  const handleClearAll = () => {
    setSelectedTypeIds([]);
    setSelectedStatusIds([]);
    setSearchValue("");
    syncToUrl({ typeIds: [], statusIds: [], search: "" });
  };

  // Allow parent to trigger clear via signal
  createEffect(() => {
    const signal = props.clearSignal;
    if (signal && signal > 0) handleClearAll();
  });

  const handleChipRemove = (key: string) => {
    if (key === "search") {
      setSearchValue("");
      syncToUrl({ ...currentFilters(), search: "" });
    } else if (key.startsWith("type:")) {
      const typeId = key.slice(5);
      const next = selectedTypeIds().filter((id) => id !== typeId);
      setSelectedTypeIds(next);
      syncToUrl({ ...currentFilters(), typeIds: next });
    } else if (key.startsWith("status:")) {
      const statusId = key.slice(7);
      const next = selectedStatusIds().filter((id) => id !== statusId);
      setSelectedStatusIds(next);
      syncToUrl({ ...currentFilters(), statusIds: next });
    }
  };

  return (
    <div class="px-6 py-2 bg-surface border-b border-line">
      {/* Controls row */}
      <div class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3">
        <SearchInput
          value={searchValue()}
          placeholder="Search by title..."
          debounceMs={300}
          onSearch={handleSearchChange}
          class="flex-1 min-w-0 max-w-none sm:max-w-sm"
        />
        <div class="flex gap-2 sm:contents">
          <MultiSelect
            options={typeOptions()}
            selected={selectedTypeIds()}
            placeholder="Type"
            onChange={handleTypeChange}
            class="flex-1 sm:flex-none sm:w-[140px]"
          />
          <MultiSelect
            options={statusOptions()}
            selected={selectedStatusIds()}
            placeholder="Status"
            onChange={handleStatusChange}
            class="flex-1 sm:flex-none sm:w-[160px]"
          />
        </div>
        <Show when={hasActiveFilters()}>
          <button
            type="button"
            class="h-7 px-2 sm:-mx-2 rounded-sm focus-ring text-sm text-ink-3 hover:text-ink transition-colors whitespace-nowrap"
            onClick={handleClearAll}
            aria-label="Clear all filters"
          >
            Clear all
          </button>
        </Show>
      </div>

      {/* Chips row */}
      <Show when={chips().length > 0}>
        <FilterChips
          chips={chips()}
          onRemove={handleChipRemove}
          onClearAll={handleClearAll}
        />
      </Show>
    </div>
  );
};

function statusDotColor(category: string): string {
  switch (category) {
    case "todo":
      return "bg-line-strong";
    case "in_progress":
      return "bg-signal-blue";
    case "done":
      return "bg-signal-green";
    case "cancelled":
      return "bg-signal-red";
    default:
      return "bg-line-strong";
  }
}
